/**
 * Inventory Service
 * API calls for inventory management
 */

import { api } from './api.js';
import type { PaginatedResponse } from '../types/api.js';
import type {
  ProductStock,
  StockAdjustment,
  StockAdjustmentInput,
  InventorySummary,
  StockMovementSummary,
  StockValuation,
  StockLevelsParams,
  AdjustmentsParams,
  LowStockParams,
} from '../types/inventory.js';

export const inventoryService = {
  async getStockLevels(params?: StockLevelsParams): Promise<PaginatedResponse<ProductStock>> {
    const response = await api.get<PaginatedResponse<ProductStock>>('/inventory/stock', { params });
    return response.data;
  },

  async getProductStock(productId: string): Promise<ProductStock> {
    const response = await api.get<ProductStock>(`/inventory/products/${productId}/stock`);
    return response.data;
  },

  async getLowStock(params?: LowStockParams): Promise<PaginatedResponse<ProductStock>> {
    const response = await api.get<PaginatedResponse<ProductStock>>('/inventory/low-stock', { params });
    return response.data;
  },

  async getAdjustments(params?: AdjustmentsParams): Promise<PaginatedResponse<StockAdjustment>> {
    const response = await api.get<PaginatedResponse<StockAdjustment>>('/inventory/adjustments', { params });
    return response.data;
  },

  async getAdjustment(adjustmentId: string): Promise<StockAdjustment> {
    const response = await api.get<StockAdjustment>(`/inventory/adjustments/${adjustmentId}`);
    return response.data;
  },

  async createAdjustment(data: StockAdjustmentInput): Promise<StockAdjustment> {
    const response = await api.post<StockAdjustment>('/inventory/adjustments', data);
    return response.data;
  },

  async createBulkAdjustments(adjustments: StockAdjustmentInput[]): Promise<StockAdjustment[]> {
    const response = await api.post<StockAdjustment[]>('/inventory/adjustments/bulk', { adjustments });
    return response.data;
  },

  async getProductAdjustmentHistory(productId: string, limit = 50): Promise<StockAdjustment[]> {
    const response = await api.get<StockAdjustment[]>(`/inventory/products/${productId}/adjustments`, {
      params: { limit },
    });
    return response.data;
  },

  async getSummary(): Promise<InventorySummary> {
    const response = await api.get<InventorySummary>('/inventory/summary');
    return response.data;
  },

  async getMovementSummary(options: {
    productId?: string;
    categoryId?: string;
    startDate: string;
    endDate: string;
    groupBy: 'day' | 'week' | 'month';
  }): Promise<StockMovementSummary[]> {
    const response = await api.get<StockMovementSummary[]>('/inventory/movement-summary', { params: options });
    return response.data;
  },

  async getValuation(categoryId?: string): Promise<StockValuation[]> {
    const response = await api.get<StockValuation[]>('/inventory/valuation', {
      params: categoryId ? { categoryId } : undefined,
    });
    return response.data;
  },
};
